import React from 'react';
import {
  Building2,
  Ship,
  Sparkles,
  Layers,
  CheckCircle2,
  AlertCircle,
  X,
  CreditCard,
  ShieldCheck,
  ArrowRight,
  Info,
} from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { IndustryType } from '../../types';

interface SectorSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSector: (sector: IndustryType, mode: 'ai' | 'standard') => void;
  onOpenBilling?: () => void;
}

export const SectorSelectionModal: React.FC<SectorSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectSector,
  onOpenBilling,
}) => {
  const { sectorQuotas, totalRemainingQuota } = useSite();

  if (!isOpen) return null;

  const corp = sectorQuotas.CORPORATE;
  const yacht = sectorQuotas.YACHT_RENTAL;

  const handleChoose = (sector: IndustryType, mode: 'ai' | 'standard') => {
    const quota = sector === 'YACHT_RENTAL' ? yacht : corp;
    if (!quota.allowed || quota.remaining <= 0) {
      return;
    }
    onClose();
    onSelectSector(sector, mode);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/80 backdrop-blur-md animate-in fade-in select-none">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col text-slate-100">
        {/* Modal Header */}
        <div className="p-6 border-b border-slate-800/80 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">Yeni Web Sitesi — Sektör Seçimi</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Oluşturmak istediğiniz web sitesi sektörünü seçin. Haklar paket bazında tanımlanır.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Informational Banner */}
        <div className="px-6 pt-5 pb-1">
          <div className="bg-slate-800/60 border border-slate-700/60 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2.5 text-slate-300">
              <Info className="w-4 h-4 text-blue-400 shrink-0" />
              <span>
                <strong>Paket ≠ Website Kuralı:</strong> Kurumsal paket hakları Yat sitesine, Yat hakları Kurumsal siteye dönüştürülemez.
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="px-2.5 py-1 rounded-lg bg-blue-500/20 text-blue-300 font-semibold border border-blue-500/30">
                Kurumsal: {corp.used}/{corp.total}
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-cyan-500/20 text-cyan-300 font-semibold border border-cyan-500/30">
                Yat: {yacht.used}/{yacht.total}
              </span>
            </div>
          </div>
        </div>

        {/* Sector Cards Grid */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card 1: Kurumsal Web Sitesi */}
          <div
            className={`rounded-3xl border p-6 flex flex-col justify-between transition relative ${
              corp.allowed && corp.remaining > 0
                ? 'bg-slate-950/80 border-blue-600/40 hover:border-blue-500 shadow-lg shadow-blue-950/30'
                : 'bg-slate-950/40 border-slate-800 opacity-90'
            }`}
          >
            <div className="space-y-4">
              {/* Header: Icon + Quota Badge */}
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-2xl bg-blue-500/15 border border-blue-500/30 text-blue-400 flex items-center justify-center">
                  <Building2 className="w-6 h-6" />
                </div>
                {corp.allowed ? (
                  corp.remaining > 0 ? (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{corp.remaining} Hak Mevcut</span>
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>Kota Dolu ({corp.used}/{corp.total})</span>
                    </span>
                  )
                ) : (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-800 text-slate-400 border border-slate-700">
                    Paket Bulunmuyor
                  </span>
                )}
              </div>

              {/* Title & Description */}
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-extrabold text-white">Kurumsal Web Sitesi</h3>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 uppercase">
                    {corp.total} Site Hakkı
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  Şirketler, ajanslar, danışmanlık firmaları ve profesyonel hizmet sağlayıcılar için modern B2B vitrini.
                </p>
              </div>

              {/* 3-Part Quota Breakdown (Requirement 23) */}
              <div className="grid grid-cols-3 gap-2 p-3 bg-slate-900/90 rounded-2xl border border-slate-800 text-center">
                <div>
                  <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Toplam Hak</div>
                  <div className="text-sm font-extrabold text-white mt-0.5">{corp.total}</div>
                </div>
                <div className="border-x border-slate-800/80">
                  <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Kullanılan</div>
                  <div className="text-sm font-extrabold text-amber-400 mt-0.5">{corp.used}</div>
                </div>
                <div>
                  <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Kalan</div>
                  <div className="text-sm font-extrabold text-emerald-400 mt-0.5">{corp.remaining}</div>
                </div>
              </div>

              {/* Features Checklist */}
              <div className="space-y-2 py-2 border-y border-slate-800/80 text-xs text-slate-300">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-blue-400 shrink-0" />
                  <span>{corp.total} Adet Bağımsız Web Sitesi Açma Hakkı</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-blue-400 shrink-0" />
                  <span>Yapay Zeka (AI) İçerik & Tasarım Sihirbazı</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-blue-400 shrink-0" />
                  <span>Görsel Editör & Sürükle-Bırak Bölüm Mimarisi</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-blue-400 shrink-0" />
                  <span>Özel Domain & SSL Entegrasyonu</span>
                </div>
              </div>

              {/* Quota Progress Bar */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Sektör Kotası Kullanımı:</span>
                  <span className="font-semibold text-slate-200">
                    {corp.used} / {corp.total} Site
                  </span>
                </div>
                <div className="h-2 w-full bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{
                      width: `${corp.total > 0 ? Math.min(100, (corp.used / corp.total) * 100) : 0}%`,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 pt-4 border-t border-slate-800/80 space-y-2">
              {corp.allowed && corp.remaining > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    id="btn-select-sector-corporate-ai"
                    onClick={() => handleChoose('CORPORATE', 'ai')}
                    className="w-full py-2.5 px-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-bold shadow-md shadow-blue-600/30 transition flex items-center justify-center gap-1.5"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>AI ile Başlat</span>
                  </button>
                  <button
                    id="btn-select-sector-corporate-standard"
                    onClick={() => handleChoose('CORPORATE', 'standard')}
                    className="w-full py-2.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition flex items-center justify-center gap-1.5"
                  >
                    <Layers className="w-3.5 h-3.5" />
                    <span>Şablon ile</span>
                  </button>
                </div>
              ) : corp.allowed && corp.remaining <= 0 ? (
                <div className="space-y-2">
                  <button
                    disabled
                    className="w-full py-2.5 px-3 rounded-xl bg-slate-800 text-slate-500 text-xs font-bold cursor-not-allowed text-center"
                  >
                    Kurumsal Hak Doldu ({corp.used}/{corp.total})
                  </button>
                  {onOpenBilling && (
                    <button
                      onClick={() => {
                        onClose();
                        onOpenBilling();
                      }}
                      className="w-full py-2 px-3 rounded-xl bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 text-xs font-semibold border border-blue-500/30 transition flex items-center justify-center gap-1.5"
                    >
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>Ek Paket Satın Al</span>
                    </button>
                  )}
                </div>
              ) : yacht.allowed ? (
                <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 text-center space-y-1.5">
                  <span className="text-xs text-amber-400 font-semibold block">
                    Bu hesapta Yat Kiralama paketi aktiftir
                  </span>
                  <p className="text-[11px] text-slate-400">
                    Kurumsal web sitesi kurmak için lütfen yeni bir Adiosoft hesabı oluşturunuz.
                  </p>
                </div>
              ) : (
                onOpenBilling && (
                  <button
                    onClick={() => {
                      onClose();
                      onOpenBilling();
                    }}
                    className="w-full py-2.5 px-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold shadow-md transition flex items-center justify-center gap-1.5"
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    <span>Kurumsal Paketi Satın Al (15.000 ₺)</span>
                  </button>
                )
              )}
            </div>
          </div>

          {/* Card 2: Lüks Yat Kiralama */}
          <div
            className={`rounded-3xl border p-6 flex flex-col justify-between transition relative ${
              yacht.allowed && yacht.remaining > 0
                ? 'bg-slate-950/80 border-cyan-600/40 hover:border-cyan-500 shadow-lg shadow-cyan-950/30'
                : 'bg-slate-950/40 border-slate-800 opacity-90'
            }`}
          >
            <div className="space-y-4">
              {/* Header: Icon + Quota Badge */}
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-2xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 flex items-center justify-center">
                  <Ship className="w-6 h-6" />
                </div>
                {yacht.allowed ? (
                  yacht.remaining > 0 ? (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{yacht.remaining} Hak Mevcut</span>
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>Kota Dolu ({yacht.used}/{yacht.total})</span>
                    </span>
                  )
                ) : (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-800 text-slate-400 border border-slate-700">
                    Paket Bulunmuyor
                  </span>
                )}
              </div>

              {/* Title & Description */}
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-extrabold text-white">Lüks Yat Kiralama</h3>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 uppercase">
                    {yacht.total} Site Hakkı
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  Mavi tur acenteleri, VIP yat filoları, gulet, motoryat ve katamaran kiralama şirketleri için özel sistem.
                </p>
              </div>

              {/* 3-Part Quota Breakdown (Requirement 23) */}
              <div className="grid grid-cols-3 gap-2 p-3 bg-slate-900/90 rounded-2xl border border-slate-800 text-center">
                <div>
                  <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Toplam Hak</div>
                  <div className="text-sm font-extrabold text-white mt-0.5">{yacht.total}</div>
                </div>
                <div className="border-x border-slate-800/80">
                  <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Kullanılan</div>
                  <div className="text-sm font-extrabold text-amber-400 mt-0.5">{yacht.used}</div>
                </div>
                <div>
                  <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Kalan</div>
                  <div className="text-sm font-extrabold text-emerald-400 mt-0.5">{yacht.remaining}</div>
                </div>
              </div>

              {/* Features Checklist */}
              <div className="space-y-2 py-2 border-y border-slate-800/80 text-xs text-slate-300">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span>{yacht.total} Adet Lüks Yat Kiralama Sitesi Açma Hakkı</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span>Filo & Yat Yönetimi (Teknik Özellikler, Donanım)</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span>Yat Arama, Çoklu Filtreleme & 2 Adımlı Rezervasyon</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span>Müsaitlik Takvimi, Mavi Tur Rotaları & Marinalar</span>
                </div>
              </div>

              {/* Quota Progress Bar */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Sektör Kotası Kullanımı:</span>
                  <span className="font-semibold text-slate-200">
                    {yacht.used} / {yacht.total} Site
                  </span>
                </div>
                <div className="h-2 w-full bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-cyan-500 transition-all duration-300"
                    style={{
                      width: `${yacht.total > 0 ? Math.min(100, (yacht.used / yacht.total) * 100) : 0}%`,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 pt-4 border-t border-slate-800/80 space-y-2">
              {yacht.allowed && yacht.remaining > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    id="btn-select-sector-yacht-ai"
                    onClick={() => handleChoose('YACHT_RENTAL', 'ai')}
                    className="w-full py-2.5 px-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-bold shadow-md shadow-cyan-600/30 transition flex items-center justify-center gap-1.5"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>AI ile Başlat</span>
                  </button>
                  <button
                    id="btn-select-sector-yacht-standard"
                    onClick={() => handleChoose('YACHT_RENTAL', 'standard')}
                    className="w-full py-2.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition flex items-center justify-center gap-1.5"
                  >
                    <Layers className="w-3.5 h-3.5" />
                    <span>Şablon ile</span>
                  </button>
                </div>
              ) : yacht.allowed && yacht.remaining <= 0 ? (
                <div className="space-y-2">
                  <button
                    disabled
                    className="w-full py-2.5 px-3 rounded-xl bg-slate-800 text-slate-500 text-xs font-bold cursor-not-allowed text-center"
                  >
                    Yat Kiralama Hak Doldu ({yacht.used}/{yacht.total})
                  </button>
                  {onOpenBilling && (
                    <button
                      onClick={() => {
                        onClose();
                        onOpenBilling();
                      }}
                      className="w-full py-2 px-3 rounded-xl bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 text-xs font-semibold border border-cyan-500/30 transition flex items-center justify-center gap-1.5"
                    >
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>Ek Yat Paketi Satın Al</span>
                    </button>
                  )}
                </div>
              ) : corp.allowed ? (
                <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 text-center space-y-1.5">
                  <span className="text-xs text-amber-400 font-semibold block">
                    Bu hesapta Kurumsal Website paketi aktiftir
                  </span>
                  <p className="text-[11px] text-slate-400">
                    Yat Kiralama web sitesi kurmak için lütfen yeni bir Adiosoft hesabı oluşturunuz.
                  </p>
                </div>
              ) : (
                onOpenBilling && (
                  <button
                    onClick={() => {
                      onClose();
                      onOpenBilling();
                    }}
                    className="w-full py-2.5 px-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold shadow-md transition flex items-center justify-center gap-1.5"
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    <span>Yat Kiralama Paketini Satın Al (36.000 ₺)</span>
                  </button>
                )
              )}
            </div>
          </div>
        </div>

        {/* Footer Summary */}
        <div className="p-6 border-t border-slate-800 bg-slate-950/60 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <div>
            Toplam Kullanılabilir Hak: <strong className="text-white">{totalRemainingQuota} Web Sitesi</strong>
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold transition"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};
